import { WaypointStatusName, DraftWaypointStatus, OfficeType } from '@caresend/types';
import { formatCamelCase } from '@caresend/utils';
import __vue_component__ from '../../../../ItineraryList/components/ExpandCopyID.vue.js';
import __vue_component__$1 from '../components/TableUserNode/TableUserNode.vue.js';
import __vue_component__$2 from './components/OrderFulfilmentStatusTags.vue.js';
import { usePortalStore } from '../../../../../store/portal/index.js';
import { TagStyle } from '../../../../../../../ui/Tag/model.js';
import { hexAlphaTransform } from '../../../../../../../../functions/color.js';
import { getStore } from '../../../../../../../../store/helpers/initStore.js';

const getDefaultFilter = () => {
    var _a;
    const store = getStore();
    if (!((_a = store.state.office) === null || _a === void 0 ? void 0 : _a.office))
        return '';
    const isStaffingOffice = store.state.office.office.type === OfficeType.STAFFING;
    if (isStaffingOffice)
        return `activeStaffingOfficeID:${store.state.office.office.id}`;
    return '';
};
const getTagInfoByFulfillmentStatus = (status) => {
    switch (status) {
        case DraftWaypointStatus.CANCELED:
            return {
                status,
                tagStyle: TagStyle['3_RED'],
                text: 'Canceled',
                colors: {
                    color: '#ffc3be',
                    textColor: '#B42318', // $cs-red-600
                },
            };
        case DraftWaypointStatus.PENDING:
            return {
                status,
                tagStyle: TagStyle['3_ORANGE'],
                text: 'Ordered',
                colors: {
                    color: hexAlphaTransform('#FEDF89', 0.3),
                    textColor: '#DC6803', // $cs-orange-400
                },
            };
        case DraftWaypointStatus.REJECTED:
            return {
                status,
                tagStyle: TagStyle['3_RED'],
                text: 'Rejected',
                colors: {
                    color: hexAlphaTransform('#ffc3be', 0.3),
                    textColor: '#B42318', // $cs-red-600
                },
            };
        case DraftWaypointStatus.FINALIZED:
        case WaypointStatusName.CREATED:
        case WaypointStatusName.IN_ITINERARY:
            return {
                status,
                tagStyle: TagStyle['3_BLUE'],
                text: 'Scheduled',
                colors: {
                    color: '#c3e6ff',
                    textColor: '#004386', // $cs-blue-600
                },
            };
        case WaypointStatusName.TRANSIT_WAYPOINT:
            return {
                status,
                tagStyle: TagStyle['3_PURPLE'],
                text: 'On the way',
                colors: {
                    color: hexAlphaTransform('#F4EBFF', 1),
                    textColor: '#53389E', // $cs-purple-800
                },
            };
        case WaypointStatusName.AT_WAYPOINT:
            return {
                status,
                tagStyle: TagStyle['3_PURPLE'],
                text: 'In progress',
                colors: {
                    color: hexAlphaTransform('#85ADFF', 0.3),
                    textColor: '#3979FA', // $cs-blue-500
                },
            };
        case WaypointStatusName.COMPLETE:
            return {
                status,
                tagStyle: TagStyle['3_GREEN'],
                text: 'Complete',
                colors: {
                    color: hexAlphaTransform('#12B76A', 0.3),
                    textColor: '#05603A', // $cs-green-800
                },
            };
        default:
            return {
                status,
                tagStyle: TagStyle['3_GREY'],
                text: formatCamelCase(status),
                colors: {
                    color: hexAlphaTransform('#081D34', 0.3),
                    textColor: '#F9FAFC', // $cs-grey-500
                },
            };
    }
};
const concatFilters = (combinator, ...filters) => filters
    .filter((s) => !!s)
    .join(` ${combinator} `);
const orderQuickFilters = () => [
    {
        label: 'All',
        filter: getDefaultFilter(),
        default: true,
    },
    {
        label: 'Ordered',
        filter: concatFilters('AND', getDefaultFilter(), `draftWaypointStatuses.status:${DraftWaypointStatus.PENDING}`),
    },
    {
        label: 'Scheduled',
        filter: concatFilters('AND', getDefaultFilter(), `draftWaypointStatuses.status:${DraftWaypointStatus.FINALIZED}`),
    },
    {
        label: 'Job created',
        filter: concatFilters('AND', getDefaultFilter(), `draftWaypointStatuses.status:${DraftWaypointStatus.IN_ITINERARY}`),
    },
    {
        label: 'On the way',
        filter: concatFilters('AND', getDefaultFilter(), `waypointStatuses.status:${WaypointStatusName.TRANSIT_WAYPOINT}`),
    },
    {
        label: 'In progress',
        filter: concatFilters('AND', getDefaultFilter(), `waypointStatuses.status:${WaypointStatusName.AT_WAYPOINT}`),
    },
    {
        label: 'Complete',
        filter: concatFilters('AND', getDefaultFilter(), `waypointStatuses.status:${WaypointStatusName.COMPLETE}`),
    },
    {
        label: 'Canceled',
        filter: concatFilters('AND', getDefaultFilter(), `draftWaypointStatuses.status:${DraftWaypointStatus.CANCELED}`),
    },
];
/** TODO: Debug why the value input for direct child attrs doesnt work */
const collectionSpec = {
    id: {
        title: 'ID',
        clickable: true,
        rowComponent: (_value, order) => ({
            component: __vue_component__,
            props: {
                id: order.id,
                entityName: 'Job',
                dark: true,
                substring: 6,
            },
            style: {
                margin: 0,
            },
        }),
        position: {
            width: { type: 'static', value: 120 },
            justify: 'center',
            textAlign: 'center',
        },
    },
    patientNames: {
        title: 'Patient(s)',
        array: {
            maxUnitLength: 1,
        },
        position: {
            width: { type: 'static', value: 200 },
        },
        rowComponent: (_value, order) => {
            const portalStore = usePortalStore();
            const patients = portalStore.orders.getters.patients(order.id);
            return {
                component: __vue_component__$1,
                props: {
                    users: patients,
                    bannerStyle: 'compact',
                    disablePopup: true,
                },
            };
        },
    },
    prescriberName: {
        title: 'Ordered by',
        position: {
            width: { type: 'static', value: 200 },
        },
    },
    nurseNames: {
        title: 'Assigned staff',
        array: {
            maxUnitLength: 1,
        },
    },
    fulfillmentEvents: {
        title: 'Fulfillment event',
        array: {
            maxUnitLength: 1,
        },
        position: {
            width: { type: 'static', value: 200 },
            justify: 'right',
        },
    },
    fulfillmentStatuses: {
        title: 'Fulfillment status',
        rowComponent: (_value, order) => {
            const portalStore = usePortalStore();
            const fulfillmentStatuses = portalStore.orders.getters
                .fulfillmentStatuses(order.id);
            return {
                component: __vue_component__$2,
                props: {
                    statuses: fulfillmentStatuses,
                    tagInfoGetter: getTagInfoByFulfillmentStatus,
                },
            };
        },
    },
};

export { collectionSpec, concatFilters, getTagInfoByFulfillmentStatus, orderQuickFilters };
